import {WBButton} from '@wandb/weave/common/components/elements/WBButtonNew';
import queryString from 'query-string';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {ExceededTeamsPlanLimitBanner} from '../../components/GlobalBanners/TeamsPlanEnforcementBanners/ExceededTeamsPlanLimitBanner';
import {ReachingTeamsPlanLimitBanner} from '../../components/GlobalBanners/TeamsPlanEnforcementBanners/ReachingTeamsPlanLimitBanner';
import {TeamMembers} from '../../components/MembersTable/TeamMembersTable';
import {TeamMemberRole} from '../../components/MembersTable/TeamMembersTable/utils';
import {PageWithSidebar} from '../../components/PageWithSidebar';
import {
  QuickStart,
  QuickStartAnalyticsOrigin,
} from '../../components/QuickStart';
import {RoutedTab} from '../../components/RoutedTabNew';
import {
  RunsTableRun,
  runsTableRunFromQueryData,
} from '../../components/RunsTable';
import {AccountSelectorContext} from '../../components/Search/SearchNav/AccountSelectorContextProvider';
import {
  SectionHeaderActions,
  SectionNew as Section,
} from '../../components/Section';
import {SectionTopHeader} from '../../components/Section/SectionNew';
import {ServiceAccountsTable} from '../../components/ServiceAccounts/ServiceAccounts';
import {TeamSettings} from '../../components/TeamSettings';
import TeamTrialNudgeBar from '../../components/TeamTrialNudgeBar';
import config from '../../config';
import {
  useEntityRunsQuery,
  useOrganizationMemberRoleQuery,
  WorkflowsAdminType,
} from '../../generated/graphql';
import {TEAM_PAGE_QUERY} from '../../graphql/teamPage';
import {TeamPageEntity} from '../../graphql/teamQuery';
import {useQuery} from '../../state/graphql/query';
import {useViewer} from '../../state/viewer/hooks';
import {Graph} from '../../types/graphql';
import {
  PremiumFeatureTooltip,
  useIsServiceAccountsEnabled,
} from '../../util/accounts/pricing';
import {useAdminModeActive} from '../../util/admin';
import {setDocumentTitle} from '../../util/document';
import {propagateErrorsContext} from '../../util/errors';
import {safeLocalStorage} from '../../util/localStorage';
import {isPublicRead} from '../../util/projectAccess';
import {teamPage} from '../../util/urls';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {
  CustomizeShowcasedReportsModalTeam,
  ProjectsSection,
  useEntityPageContext,
} from '../EntityPage';
import {EmptyState} from '../EntityPage/EmptyState';
import ReportsTable from '../EntityPage/ReportsTable';
import {ResourceSubscriptionHasBeenUpgradedBannerGlobalList} from './../../components/GlobalBanners/StorageEnforcementBanners/ResourceSubscriptionHasBeenUpgradedBanner';
import {LoaderProxy} from './../../containers/AppLoader';
import {EntityOverviewPage} from './EntityOverviewPage';
import {MimicTable} from './MimicTable';
import TeamInviteModal from './TeamInviteModal';
import * as S from './TeamPage.styles';
import {TeamSidebar} from './TeamSidebar';
import {TeamPageQueryData, TeamPageQueryVariables} from './types';
import {useTeamPageProjects} from './useTeamProjects';

interface TeamPageProps {
  entityName: string;
  tab?: string;
}

export const TeamPage: FC<TeamPageProps> = React.memo(({entityName, tab}) => {
  const viewer = useViewer();

  const [inviteQueryParam, setInviteQueryParam] = useState('');
  const [isNewTeam, setIsNewTeam] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const isAdminModeActive = useAdminModeActive();

  useEffect(() => {
    setDocumentTitle(entityName);
  }, [entityName]);

  const teamPageQuery = useQuery<TeamPageQueryData, TeamPageQueryVariables>(
    TEAM_PAGE_QUERY,
    {
      variables: {entityName, workflowsAdminType: WorkflowsAdminType.Model},
    }
  );
  const {data: teamPageProjectsData, loading: teamPageProjectsLoading} =
    useTeamPageProjects(entityName);

  const data: TeamPageEntity | null = useMemo(() => {
    if (teamPageQuery.loading) {
      return null;
    }

    if (!teamPageQuery.data?.entity) {
      return null;
    }

    return teamPageQuery.data.entity;
  }, [teamPageQuery.loading, teamPageQuery.data]);

  const {data: orgMemberRoleData} = useOrganizationMemberRoleQuery({
    variables: {
      orgID: data?.organizationId ?? '',
      username: viewer?.username ?? '',
    },
    skip: !data?.organizationId || !viewer?.id,
  });

  const reportEdges = {
    edges:
      teamPageProjectsData?.entity?.runs.map(r => ({
        node: r,
      })) ?? [],
  };

  const {data: entityRunsData, loading: isEntityRunsLoading} =
    useEntityRunsQuery({
      variables: {entityName},
      context: propagateErrorsContext(),
    });

  const runData: Graph<RunsTableRun> = runsTableRunFromQueryData(
    entityRunsData?.entity?.latestRuns
  );

  const viewerIsMember =
    viewer != null &&
    data != null &&
    data.members.some(m => m.id === viewer.id);

  const viewerIsOrgAdmin =
    orgMemberRoleData?.organization?.member?.role === 'admin';

  const viewerIsTeamAdmin = data?.members.some(
    m => m.username === viewer?.username && m.role === TeamMemberRole.ADMIN
  );

  useEffect(() => {
    const {
      invited: invitedParam,
      newUser: newUserParam,
      newTeam: newTeamParam,
    }: queryString.ParsedQuery<string> = queryString.parse(
      document.location.search
    );
    // On first render, if the URL indicates that the user is accepting
    // the team invite, open the modal to update default entity
    if (!!invitedParam && inviteQueryParam === '') {
      const invited: string =
        typeof invitedParam === 'string' ? invitedParam : invitedParam[0];
      safeLocalStorage.setItem('auth.invited', invited);
      setInviteQueryParam(invited);

      window.analytics?.track('Invite Accepted', {
        entityName,
        location: 'team page',
      });
    }

    if (newUserParam) {
      const isNew =
        typeof newUserParam === 'string'
          ? newUserParam === 'true'
          : newUserParam[0] === 'true';
      setIsNewUser(isNew);
    }

    if (newTeamParam) {
      setIsNewTeam(true);
    }
  }, [entityName, inviteQueryParam]);

  useEffect(() => {
    if (!isEntityRunsLoading && data != null) {
      const runs = entityRunsData?.entity?.latestRuns ?? {edges: []};
      window.analytics?.track('Team Overview Viewed', {
        entityName,
        'empty-state': !runs.edges.length && !data.projects?.edges?.length,
        runCount: runs.edges.length,
        projectCount: teamPageProjectsData?.entity?.projects.length,
        reportCount: teamPageProjectsData?.entity?.runs.length,
      });
    }
  }, [
    entityName,
    entityRunsData,
    isEntityRunsLoading,
    data,
    teamPageProjectsData?.entity,
  ]);

  const {reportSpec, showcasedProjectIDs, showcasedReportIDs, links} =
    useEntityPageContext();

  const [
    customizeShowcasedReportsModalOpen,
    setCustomizeShowcasedReportsModalOpen,
  ] = useState(false);

  const openCustomizeShowcasedReportsModal = useCallback(() => {
    setCustomizeShowcasedReportsModalOpen(true);
  }, []);

  const closeCustomizeShowcasedReportsModal = useCallback(() => {
    setCustomizeShowcasedReportsModalOpen(false);
  }, []);

  const {selectedAccount} = useContext(AccountSelectorContext);
  // technically you should have a valid license to use service accounts in onprem environments,
  // but since you can't create teams without a license anyway, checking the config is fine for now until we
  // get account selector in server
  const hasServiceAccountAccess =
    useIsServiceAccountsEnabled(selectedAccount) ||
    config.ENVIRONMENT_IS_PRIVATE;

  if (teamPageQuery.loading || data == null) {
    return <LoaderProxy name="team-page-loader-new" />;
  }

  const {name: teamName, readOnly} = data;

  // by checking for _at least_ one project, we know it's not a new team
  const {projectCount} = data;

  const projects = teamPageProjectsData?.entity.projects ?? [];

  const teamHasPublicReports =
    teamPageProjectsData?.entity?.runs.some(r => isPublicRead(r.project)) ??
    false;

  const projectToCreateReportFor = projects.find(isPublicRead) ?? null;

  const showProjectsSectionEmptyState =
    !readOnly && showcasedProjectIDs.length === 0;

  const showReportsSectionEmptyState =
    !readOnly &&
    showcasedReportIDs.length === 0 &&
    (teamHasPublicReports || projectToCreateReportFor != null);

  const showLinksSectionEmptyState = !readOnly && links.length === 0;

  const showQuickstart =
    !readOnly &&
    projectCount === 0 &&
    !['members', 'service-accounts', 'settings'].includes(tab ?? 'overview');

  const showIntroSection = !readOnly || reportSpec != null;
  const showShowcasedProjectsSection =
    showcasedProjectIDs.length > 0 || showProjectsSectionEmptyState;
  const showShowcasedReportsSection =
    showcasedReportIDs.length > 0 || showReportsSectionEmptyState;
  const showLinksSection = links.length > 0 || showLinksSectionEmptyState;
  const showRunsSection = runData.edges.length > 0;

  const showOverviewTabEmptyState =
    !showIntroSection &&
    !showShowcasedProjectsSection &&
    !showShowcasedReportsSection &&
    !showLinksSection &&
    !showRunsSection;
  const showReportsTabEmptyState =
    !teamPageProjectsLoading && reportEdges.edges.length === 0;

  const showProjectsTabEmptyState =
    !teamPageProjectsLoading &&
    (teamPageProjectsData == null ||
      teamPageProjectsData.entity.projects.length === 0);
  const showMembersTab =
    viewerIsMember || isAdminModeActive || viewerIsOrgAdmin;
  const viewerIsAdmin = isAdminModeActive || viewerIsTeamAdmin;
  const showSettingsTab = viewerIsAdmin || viewerIsOrgAdmin;

  const showUniversalEmptyState =
    showOverviewTabEmptyState &&
    showReportsTabEmptyState &&
    showProjectsTabEmptyState &&
    !showMembersTab;

  const emptyStateMessage =
    viewerIsOrgAdmin && !viewerIsMember
      ? 'You do not have permissions to access this team as an org admin. You can add yourself from the organization-level Teams tab or team-level Members tab if you should have access to this team.'
      : 'This team has no showcased content';

  const teamRunData = runsTableRunFromQueryData(
    entityRunsData?.entity?.latestRuns
  );
  return (
    <S.Wrapper data-test="team-page">
      <PageWithSidebar
        sidebar={
          <TeamSidebar
            members={data.members}
            teamData={data}
            refetch={teamPageQuery.refetch}
          />
        }
        contentStyle={{
          backgroundColor: showQuickstart ? 'white' : 'unset',
        }}>
        <>
          <div style={{maxWidth: '1000px'}}>
            <ResourceSubscriptionHasBeenUpgradedBannerGlobalList />
            <ReachingTeamsPlanLimitBanner />
            <ExceededTeamsPlanLimitBanner />
          </div>
          {showQuickstart ? (
            <div style={{width: '100%'}}>
              <TeamTrialNudgeBar entityName={entityName} />
              <QuickStart
                analyticsOrigin={QuickStartAnalyticsOrigin.TEAM_PAGE}
              />
            </div>
          ) : showUniversalEmptyState ? (
            <Section>
              <EmptyState
                iconName="lock-closed"
                header={`This team has no public content`}
                withExtraPadding
              />
            </Section>
          ) : (
            <RoutedTab
              style={{maxWidth: '1000px'}}
              baseUrl={teamPage(teamName)}
              tabSlugs={[
                'overview',
                'reports',
                'projects',
                ...(showMembersTab ? ['members'] : []),
                ...(showMembersTab ? ['service-accounts'] : []),
                ...(showSettingsTab ? ['settings'] : []),
              ]}
              activeTabSlug={tab}
              panes={[
                {
                  menuItem: 'Overview',
                  render: () =>
                    showOverviewTabEmptyState ? (
                      <Section>
                        <EmptyState
                          iconName="lock-closed"
                          header={emptyStateMessage}
                          withExtraPadding
                        />
                      </Section>
                    ) : (
                      <>
                        {showIntroSection && (
                          <EntityOverviewPage
                            entityName={entityName}
                            runs={teamRunData}
                            runsIsLoading={isEntityRunsLoading}
                            analyticsLocation={'team page new'}
                            isTeamPage={true}
                          />
                        )}
                      </>
                    ),
                },
                {
                  menuItem: 'Reports',
                  render: () => (
                    <>
                      {showReportsTabEmptyState ? (
                        <Section>
                          <EmptyState
                            iconName="lock-closed"
                            header={`This team has no public reports`}
                            withExtraPadding
                          />
                        </Section>
                      ) : (
                        <>
                          <SectionTopHeader>
                            <div>Reports</div>
                            {!readOnly && teamHasPublicReports && (
                              <SectionHeaderActions>
                                <WBButton
                                  onClick={openCustomizeShowcasedReportsModal}>
                                  Select reports to showcase
                                </WBButton>
                              </SectionHeaderActions>
                            )}
                          </SectionTopHeader>
                          {teamPageProjectsLoading ? (
                            <MimicTable
                              columnNames={[
                                'Title',
                                '',
                                'Project',
                                'Author',
                                'Last Edit',
                              ]}
                              columnSizes={'22fr 4.5fr 15fr 13fr 13fr'}
                              numRows={10}
                            />
                          ) : (
                            <ReportsTable
                              data={reportEdges}
                              pageSize={10}
                              suppressHeader
                              isTeamPage={true}
                            />
                          )}
                        </>
                      )}
                      {customizeShowcasedReportsModalOpen && (
                        <CustomizeShowcasedReportsModalTeam
                          onClose={closeCustomizeShowcasedReportsModal}
                        />
                      )}
                    </>
                  ),
                },
                {
                  menuItem: 'Projects',
                  render: () => (
                    <ProjectsSection
                      entityName={teamName}
                      readOnly={readOnly}
                    />
                  ),
                },
                ...(showMembersTab
                  ? [
                      {
                        menuItem: 'Users',
                        render: () => (
                          <TeamMembers
                            entity={data}
                            // TODO(elaina): we should not refetch the entire team page query here
                            refetch={teamPageQuery.refetch}
                          />
                        ),
                      },
                    ]
                  : []),
                ...(showMembersTab
                  ? [
                      {
                        menuItem: {
                          key: 'service-accounts',
                          content: (
                            <>
                              Service Accounts &nbsp;
                              <PremiumFeatureTooltip
                                isPremiumFeatureDisabled={
                                  !hasServiceAccountAccess
                                }
                              />
                            </>
                          ),
                        },
                        render: () => (
                          <ServiceAccountsTable
                            entity={data}
                            // TODO(elaina): we should not refetch the entire team page query here
                            refetchTeamData={teamPageQuery.refetch}
                          />
                        ),
                      },
                    ]
                  : []),
                ...(showSettingsTab
                  ? [
                      {
                        menuItem: 'Settings',
                        render: () => (
                          <TeamSettings entityName={teamName} isTeam />
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          )}
          {(inviteQueryParam || isNewTeam || isNewUser) && (
            <TeamInviteModal
              inviteQueryParam={inviteQueryParam}
              isNewUser={isNewUser}
              isNewTeam={isNewTeam}
              setInviteQueryParam={setInviteQueryParam}
              teamName={teamName}
            />
          )}
        </>
      </PageWithSidebar>
    </S.Wrapper>
  );
});
TeamPage.displayName = 'TeamPage';
